import { HOME_FAQ_ITEMS, HOME_FEATURED_ARTICLES } from './homeContent';
import { SiteSettings } from '../services/api';
import {
  CLINIC_SCHEMA,
  DEFAULT_OG_IMAGE,
  ORGANIZATION_ID,
  SITE_NAME,
  SITE_URL,
  WEBSITE_ID,
  articleUrl,
} from './seo';

export const MEDICAL_CLINIC_ID = `${SITE_URL}/#medicalclinic`;
export const HOME_FAQ_ID = `${SITE_URL}/#faq`;
export const HOME_FEATURED_ARTICLES_ID = `${SITE_URL}/#featured-articles`;

export interface DynamicFaqItem {
  question: string;
  answer: string;
}

export interface DynamicArticleItem {
  title: string;
  slug: string;
}

export interface DynamicServiceItem {
  title: string;
}

export function buildHomeFaqSchemaNode(faqs?: DynamicFaqItem[]) {
  const items = faqs && faqs.length > 0 ? faqs : HOME_FAQ_ITEMS;
  return {
    '@type': 'FAQPage',
    '@id': HOME_FAQ_ID,
    mainEntity: items.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };
}

export function buildHomeFeaturedArticlesItemListNode(articles?: DynamicArticleItem[]) {
  const items = articles && articles.length > 0 ? articles : HOME_FEATURED_ARTICLES;
  return {
    '@type': 'ItemList',
    '@id': HOME_FEATURED_ARTICLES_ID,
    name: 'Dental Health Tips & Articles',
    itemListElement: items.map((article, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: article.title,
      item: {
        '@type': 'BlogPosting',
        url: articleUrl(article.slug),
        headline: article.title,
      },
    })),
  };
}

export function buildHomePageSchemaGraph(
  settings?: SiteSettings | null,
  services?: DynamicServiceItem[],
  faqs?: DynamicFaqItem[],
  articles?: DynamicArticleItem[]
) {
  const clinicName = settings?.clinicName || CLINIC_SCHEMA.name;
  const telephone = settings?.phone || CLINIC_SCHEMA.telephone;
  const email = settings?.email || CLINIC_SCHEMA.email;
  const streetAddress = settings?.address || CLINIC_SCHEMA.address.streetAddress;

  const rawSocials = settings
    ? [
        settings.facebookLink,
        settings.instagramLink,
        settings.youtubeLink,
        settings.linkedinLink,
        ...CLINIC_SCHEMA.sameAs.filter((socialUrl) => socialUrl !== SITE_URL),
      ].filter(Boolean)
    : CLINIC_SCHEMA.sameAs.filter((socialUrl) => socialUrl !== SITE_URL);

  const sameAs = Array.from(new Set(rawSocials));

  const hoursSpec = CLINIC_SCHEMA.openingHours.map((spec) => ({
    '@type': 'OpeningHoursSpecification',
    dayOfWeek: spec.dayOfWeek,
    opens: spec.opens,
    closes: spec.closes,
  }));

  const offerList = services && services.length > 0
    ? services.map((s) => ({ '@type': 'Offer', itemOffered: { '@type': 'Service', name: s.title } }))
    : [
        { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Teeth Cleaning & Scaling' } },
        { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Dental Implants' } },
        { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Orthodontics & Braces' } },
        { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Root Canal Treatment' } },
        { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Teeth Whitening' } },
        { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Emergency Dental Care' } },
      ];

  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        '@id': ORGANIZATION_ID,
        name: clinicName,
        alternateName: CLINIC_SCHEMA.alternateName,
        url: SITE_URL,
        logo: {
          '@type': 'ImageObject',
          url: DEFAULT_OG_IMAGE,
          width: 192,
          height: 192,
        },
        image: CLINIC_SCHEMA.image,
        telephone: telephone,
        email: email,
        sameAs: sameAs,
      },
      {
        '@type': 'DentalClinic',
        '@id': MEDICAL_CLINIC_ID,
        name: clinicName,
        alternateName: CLINIC_SCHEMA.alternateName,
        image: CLINIC_SCHEMA.image,
        url: SITE_URL,
        telephone: telephone,
        email: email,
        parentOrganization: { '@id': ORGANIZATION_ID },
        address: {
          '@type': 'PostalAddress',
          streetAddress: streetAddress,
          addressLocality: CLINIC_SCHEMA.address.addressLocality,
          addressRegion: CLINIC_SCHEMA.address.addressRegion,
          postalCode: CLINIC_SCHEMA.address.postalCode,
          addressCountry: CLINIC_SCHEMA.address.addressCountry,
        },
        geo: {
          '@type': 'GeoCoordinates',
          latitude: CLINIC_SCHEMA.geo.latitude,
          longitude: CLINIC_SCHEMA.geo.longitude,
        },
        openingHoursSpecification: hoursSpec,
        description: CLINIC_SCHEMA.description,
        priceRange: CLINIC_SCHEMA.priceRange,
        sameAs: sameAs,
        areaServed: [
          { '@type': 'City', name: 'Lalitpur' },
          { '@type': 'City', name: 'Kathmandu' },
          { '@type': 'City', name: 'Patan' },
        ],
        hasOfferCatalog: {
          '@type': 'OfferCatalog',
          name: 'Dental Services',
          itemListElement: offerList,
        },
      },
      {
        '@type': 'WebSite',
        '@id': WEBSITE_ID,
        url: SITE_URL,
        name: SITE_NAME,
        description: CLINIC_SCHEMA.description,
        publisher: { '@id': ORGANIZATION_ID },
        inLanguage: 'en',
      },
      buildHomeFaqSchemaNode(faqs),
      buildHomeFeaturedArticlesItemListNode(articles),
    ],
  };
}

/** @deprecated Use buildHomePageSchemaGraph — FAQ is included in the homepage @graph */
export function buildHomeFaqSchema(faqs?: DynamicFaqItem[]) {
  return {
    '@context': 'https://schema.org',
    ...buildHomeFaqSchemaNode(faqs),
  };
}
